import React from 'react'
import cx from 'classnames'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'

import styles from './ImportKB.module.css'

import HFLogo from 'Logos/happyfox.svg'
import ZendeskLogo from 'Logos/zendesk.svg'
import CSVFileIcon from 'Icons/file.svg'
import DividerImage from './divider.svg'

import Heading from 'Design/components/Heading'
import FormControl from 'Design/components/FormControl'
import Label from 'Design/components/Label'
import Input from 'Design/components/Input'
import Button from 'Design/components/Button'
import InputFileUpload from 'Design/components/InputFileUpload'
import FormErrorText from 'Design/components/FormErrorText'

import importKbFormValidationSchema from './importkb-form-validation-schema'
import { useLinkKbMutation } from '../kb.service'
import BackButton from '../../components/BackButton'

const kbSources = [
  {
    type: 'happyfox_helpdesk',
    title: 'HappyFox',
    description: 'Import articles from your HappyFox Knowledge Base',
    placeholder: 'https://acme.happyfox.com',
    icon: HFLogo
  },
  {
    type: 'zendesk_support',
    title: 'Zendesk',
    description: 'Import articles from your Zendesk Knowledge Base',
    placeholder: 'https://acme.zendesk.com',
    icon: ZendeskLogo
  },
  {
    type: 'csv_file',
    title: 'File',
    description: 'Import articles from a CSV file following <a href="#csvtemplate">this template</a>',
    placeholder: 'Please select a CSV file',
    icon: CSVFileIcon
  }
]

const getCSVLink = () => {
  const csvColumns = ['Reference', 'Title', 'Content', 'Tags', 'Group']
  const csvFile = new Blob([csvColumns], { type: 'text/csv' })
  const csvLink = window.URL.createObjectURL(csvFile)
  return csvLink
}

const createMarkup = (text) => {
  let content = text

  if (text.includes('#csvtemplate')) {
    content = text.replace('#csvtemplate', getCSVLink())
  }

  return { __html: content }
}

const getPlaceholder = (type) => {
  const source = kbSources.find((kb) => kb.type === type)
  return source?.placeholder ?? ''
}

const ImportKB = (props) => {
  const { hideBackButton = false } = props

  const {
    register,
    control,
    handleSubmit,
    watch,
    resetField,
    formState: { errors }
  } = useForm({
    defaultValues: {
      type: kbSources[0].type,
      url: '',
      name: '',
      file: null
    },
    shouldUnregister: true,
    resolver: yupResolver(importKbFormValidationSchema)
  })

  const navigate = useNavigate()
  const [linkKb, linkKbResult] = useLinkKbMutation()

  const handleFormSubmit = (data) => {
    let payload

    if (data.type === 'csv_file') {
      payload = new FormData()

      payload.append('name', data.name)
      payload.append('kb_type', data.type)
      payload.append('file', data.file[0])
    } else {
      payload = {
        name: data.name,
        url: data.url,
        kb_type: data.type
      }
    }

    const promise = linkKb(payload).unwrap()
    toast.promise(promise, {
      loading: `Verifying and adding "${data.name}"...`,
      success: `Started syncing "${data.name}"`,
      error: `Unable to add "${data.name}". Please try again`
    })
    promise.then(() => {
      navigate('../')
    })
  }

  const activeKbType = watch('type')

  return (
    <div className={styles.GridContainer}>
      {!hideBackButton && (
        <div className={styles.BackButtonContainer}>
          <BackButton />
        </div>
      )}
      <form className={styles.Form} onSubmit={handleSubmit(handleFormSubmit)}>
        <Heading className={styles.Title}>Import Content From Your Knowledge Base</Heading>

        <div className={styles.Importer}>
          <Controller
            name='type'
            control={control}
            render={({ field: { value, onChange } }) => {
              return (
                <div className={styles.SourceList}>
                  {kbSources.map((kb) => {
                    const Icon = kb.icon

                    return (
                      <div
                        key={'KBSourceListImporter' + kb.type}
                        className={cx(styles.SourceItem, { [styles.ActiveSourceItem]: value === kb.type })}
                        onClick={() => onChange(kb.type)}
                      >
                        <div className={styles.SourceIconContainer}>
                          <Icon height='40' width='40' />
                        </div>
                        <div className={styles.SouceDetails}>
                          <div className={styles.SourceTitle}>{kb.title}</div>
                          <div
                            className={styles.SourceDescription}
                            dangerouslySetInnerHTML={createMarkup(kb.description)}
                          />
                        </div>
                      </div>
                    )
                  })}
                </div>
              )
            }}
          />

          <div className={styles.Divider}>
            <DividerImage />
          </div>

          <div className={styles.ImporterActions}>
            {activeKbType === 'csv_file' && (
              <FormControl className={styles.FormControl} isInvalid={errors.file}>
                <Label>Upload a file with your articles</Label>
                <InputFileUpload
                  accept='.csv'
                  placeholder={getPlaceholder(activeKbType)}
                  {...register('file')}
                  onRemove={() => resetField('file')}
                />
                {errors.file && <FormErrorText>{errors.file.message}</FormErrorText>}
              </FormControl>
            )}
            {activeKbType !== 'csv_file' && (
              <FormControl className={styles.FormControl} isInvalid={errors.url}>
                <Label>Enter URL to your Knowledge Base</Label>
                <Input placeholder={getPlaceholder(activeKbType)} {...register('url', { shouldUnregister: true })} />
                {errors.url && <FormErrorText>{errors.url.message}</FormErrorText>}
              </FormControl>
            )}

            <FormControl className={styles.FormControl} isInvalid={errors.name}>
              <Label>Give this knowledge base a name</Label>
              <Input placeholder='Support Articles' {...register('name')} />
              {errors.name && <FormErrorText>{errors.name.message}</FormErrorText>}
            </FormControl>

            <Button type='submit' variant='primary' disabled={linkKbResult.isLoading}>
              Import
            </Button>
          </div>
        </div>
      </form>
    </div>
  )
}

export default ImportKB
